import React, { useState, useEffect } from "react";

import { Table, Button, Upload, Input } from "antd";
import type { ColumnType } from "antd/es/table";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Papa from "papaparse";

interface RowData {
  key: number;
  [key: string]: unknown;
}

type ColumnConfig = ColumnType<RowData>;

export default function JulieFeiComponent() {
  const [tableData, setTableData] = useState<RowData[]>([]);
  const [columns, setColumns] = useState<ColumnConfig[]>([]);
  const [tableHeight, setTableHeight] = useState(window.innerHeight - 500);

  useEffect(() => {
    const handleResize = () => {
      setTableHeight(window.innerHeight - 250);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: FilterDropdownProps) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => confirm()}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Search
        </Button>
        <Button
          onClick={() => clearFilters && clearFilters()}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    ),
    onFilter: (value: boolean | React.Key, record: RowData) =>
      record[dataIndex]
        ? record[dataIndex]!.toString()
            .toLowerCase()
            .includes(value.toString().toLowerCase())
        : false,
    filtered: false,
  });

  const getSorter = (header: string, rows: RowData[]) => {
    const sampleValues = rows
      .slice(0, 5)
      .map((row) => row[header])
      .filter((val) => val !== "");

    const isNumber = sampleValues.every((val) => !isNaN(Number(val)));
    const isDate = sampleValues.every((val) => !isNaN(Date.parse(String(val))));

    return {
      sorter: (a: RowData, b: RowData) => {
        const aVal = a[header];
        const bVal = b[header];

        if (isNumber) {
          return Number(aVal) - Number(bVal);
        }
        if (isDate) {
          return (
            new Date(String(aVal)).getTime() - new Date(String(bVal)).getTime()
          );
        }
        return (aVal || "").toString().localeCompare((bVal || "").toString());
      },
      sortDirections: ["ascend", "descend", "ascend"],
      showSorterTooltip: true,
    };
  };

  const formatHeader = (header: string): string => {
    if (!header) return "";

    const isAllCaps = header === header.toUpperCase();
    const initialProcessed = isAllCaps ? header.toLowerCase() : header;

    const words = initialProcessed
      .replace(/([A-Z])/g, " $1")
      .replace(/[_-]/g, " ")
      .toLowerCase()
      .split(" ")
      .filter((word) => word.length > 0);
    return words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleFileUpload = (file: File) => {
    Papa.parse(file, {
      complete: (results: Papa.ParseResult<unknown>) => {
        if (!results.data || !results.data.length) {
          console.error("No data found in CSV");
          return;
        }

        const validData = (results.data as unknown[][]).filter(
          (row) =>
            Array.isArray(row) && row.length > 0 && row.some((cell) => cell),
        );

        if (!validData.length) {
          console.error("No valid data rows found in CSV");
          return;
        }

        const headers = validData[0] as string[];
        const rows = validData.slice(1) as string[][];

        if (!Array.isArray(headers)) {
          console.error("Invalid headers format");
          return;
        }

        const tableRows: RowData[] = rows.map((row, index) => {
          const rowData: RowData = { key: index };
          headers.forEach((header, i) => {
            rowData[header || "unnamed"] = row[i] || "";
          });
          return rowData;
        });

        const tableColumns: ColumnConfig[] = headers.map((header, index) => {
          const formattedTitle = formatHeader(header);
          const columnConfig = {
            title: formattedTitle,
            dataIndex: header || "unnamed",
            key: header || "unnamed",
            ...getColumnSearchProps(header || "unnamed"),
            ...getSorter(header, tableRows),
            filteredValue: null as null,
            ellipsis: {
              showTitle: true,
            },
            fixed: undefined as ColumnConfig["fixed"],
            width: undefined as number | undefined,
            // width: 200,
            // onHeaderCell: () => ({
            //   style: {
            //     whiteSpace: "normal",
            //     wordBreak: "break-word",
            //     lineHeight: "1.3",
            //     fontSize: "13px",
            //     maxHeight: "35px", // Approx 2 lines
            //     overflow: "hidden",
            //     padding: "6px 8px",
            //   },
            // }),
          };

          if (index === 0) {
            columnConfig.fixed = "left";
            columnConfig.width = 150;
          }

          return columnConfig as ColumnConfig;
        });

        setColumns(tableColumns);
        setTableData(tableRows);
      },
      header: false,
      skipEmptyLines: true,
    });
  };

  return (
    <div className="m-6">
      <Upload
        accept=".csv"
        showUploadList={false}
        beforeUpload={(file) => {
          handleFileUpload(file);
          return false;
        }}
      >
        <Button className="mb-4">Import Your CSV</Button>
      </Upload>
      <div style={{ overflow: "auto", borderRadius: "8px" }}>
        <Table
          className="shadow-md"
          columns={columns}
          dataSource={tableData}
          pagination={false}
          scroll={{ x: "max-width", y: tableHeight }}
          size="small"
          footer={() => (
            <div className="flex gap-4">
              <p className="font-bold">Total records: {tableData.length}</p>
            </div>
          )}
        />
      </div>
    </div>
  );
}
